import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { TShirtIcon } from '@phosphor-icons/react'

import { BackListButton } from '@reveni-io/reveni-ui'

import FiltersSection from '@/common/components/FiltersSection/FiltersSection'
import AppContext from '@/common/context/app.context'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import MostReturnedCard from '@/metrics/components/MostReturnedCard/MostReturnedCard'
import Product from '@/metrics/components/Product/Product'
import ProductMetrics from '@/metrics/components/ProductMetrics/ProductMetrics'
import { groupByValues } from '@/metrics/constants'
import { getTitleByOrdering } from '@/metrics/helpers/helpers'
import useFilters from '@/metrics/hooks/useFilters/useFilters'
import { entityTypes } from '@/metrics/hooks/useMetrics/constants'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import metricsServices from '@/metrics/services/services'
import routes from '@/Router/routes.constants'

import * as Styles from './styles'

const ProductByCollectionMetricsDetailPage = ({ isUnifiedDashboard }) => {
  const { collectionPosition, productPosition } = useParams()
  const { info } = useContext(AppContext)
  const { t } = useTranslation()
  const { regions } = useRegions()

  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info?.selectedReturnPortal?.id })
  const salesOrdersDataActivated = returnPortalFeatures?.sales_orders_data_activated
  const {
    commonFilters,
    setCommonFilters,
    getUrlSearchParams,
    productsFilters,
    returnSalesRatioFilters,
    setReturnSalesRatioFilters,
    returnsAndExchangesRatioFilters,
    setReturnsAndExchangesRatioFilters,
  } = useFilters()

  const { data: collections, loading: loadingCollection } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getTopReturnedProducts,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: { groupBy: groupByValues.collections, ordering: productsFilters?.ordering },
    doRequest: !!collectionPosition,
  })

  const collection = collections?.results?.[collectionPosition]

  const { data: topReturnedProducts, loading: loadingTopReturnedProducts } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getCollectionTopReturnedProducts,
    entityId: collection?.id,
    entityType: entityTypes.collection,
    filters: commonFilters,
    additionalFilters: { ordering: productsFilters?.ordering },
    doRequest: !!collection?.id,
  })

  const dashboardPath = isUnifiedDashboard ? routes.merchantDashboardPage : routes.dashboardPage
  const collectionPath = isUnifiedDashboard
    ? routes.merchantDashboardCollectionMetricsDetailPage
    : routes.collectionMetricsDetailPage

  const getDashboardUrl = () => {
    return `${dashboardPath}?${getUrlSearchParams()}`
  }

  const getBreadcrumbs = () => {
    return [
      {
        title: `#${Number(collectionPosition) + 1} ${t('Colección')}`,
        subtitle: collection?.name,
        url: `${collectionPath.replace(':collectionPosition', collectionPosition)}?${getUrlSearchParams()}`,
      },
      {
        title: `#${Number(productPosition) + 1} ${t('Producto')}`,
        subtitle: topReturnedProducts?.results?.[productPosition]?.name,
      },
    ]
  }

  return (
    <Styles.Container>
      <Styles.HeaderContainer>
        <BackListButton to={getDashboardUrl()}>{t('Volver al dashboard')}</BackListButton>
        <FiltersSection regions={regions} filters={commonFilters} onChange={setCommonFilters} />
      </Styles.HeaderContainer>
      <MostReturnedCard
        Icon={TShirtIcon}
        title={getTitleByOrdering('Colecciones', productsFilters?.ordering, t)}
        loading={loadingCollection || loadingTopReturnedProducts}
        item={
          topReturnedProducts?.results?.[productPosition] && (
            <Product
              product={{ ...topReturnedProducts?.results?.[productPosition], index: Number(productPosition) + 1 }}
              showAmounts={false}
            />
          )
        }
        count={topReturnedProducts?.results?.[productPosition]?.count}
        percentage={topReturnedProducts?.results?.[productPosition]?.percentage}
        percentageText={t('colección')}
        breadcrumbs={getBreadcrumbs()}
        sales={topReturnedProducts?.results?.[productPosition]?.sales}
        salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
      />

      {topReturnedProducts?.results?.[productPosition] && (
        <ProductMetrics
          loading={loadingCollection || loadingTopReturnedProducts}
          returnPortalId={isUnifiedDashboard ? null : info?.selectedReturnPortal?.id}
          collectionId={collection?.id}
          collectionPosition={collectionPosition}
          product={topReturnedProducts?.results?.[productPosition]}
          productPosition={productPosition}
          commonFilters={commonFilters}
          returnSalesRatioFilters={returnSalesRatioFilters}
          setReturnSalesRatioFilters={setReturnSalesRatioFilters}
          returnsAndExchangesRatioFilters={returnsAndExchangesRatioFilters}
          setReturnsAndExchangesRatioFilters={setReturnsAndExchangesRatioFilters}
          storeCurrency={info?.selectedStore?.currency}
          variantsOrdering={productsFilters?.ordering}
          salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
        />
      )}
    </Styles.Container>
  )
}

export default ProductByCollectionMetricsDetailPage
